import React from 'react';
import { Container, Typography, Box, Grid, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles';

const skillCategories = [
  {
    title: 'Languages',
    color: '#6366f1',
    skills: ['JavaScript (ES6+)', 'TypeScript', 'Python', 'SQL'],
  },
  {
    title: 'Frontend',
    color: '#06b6d4',
    skills: ['React.js', 'Next.js', 'Material UI', 'Tailwind CSS', 'Responsive Design'],
  },
  {
    title: 'Backend',
    color: '#8b5cf6',
    skills: ['Node.js', 'Express.js', 'Django', 'Django REST Framework', 'FastAPI', 'REST APIs', 'JWT', 'OAuth2'],
  },
  {
    title: 'AI & Automation',
    color: '#f97316',
    skills: ['LangChain', 'LangGraph', 'RAG Pipelines', 'MCP Servers', 'n8n', 'TensorFlow', 'YOLOv8', 'OpenCV'],
  },
  {
    title: 'Databases',
    color: '#10b981',
    skills: ['PostgreSQL', 'MongoDB', 'Prisma ORM', 'Mongoose'],
  },
  {
    title: 'Cloud & DevOps',
    color: '#f59e0b',
    skills: ['AWS', 'Azure Blob Storage', 'Docker', 'Vercel', 'Railway', 'Render', 'CI/CD', 'Git'],
  },
  {
    title: 'Tools & Practices',
    color: '#ec4899',
    wide: true,
    skills: ['Postman', 'Prisma Migrations', 'Agile / Scrum', 'RBAC', 'Microservices', 'API Integration', 'MVC Architecture', 'Cursor Pagination'],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const categoryVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const skillVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

function Skills() {
  return (
    <Container id="skills" maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 700,
              letterSpacing: '0.15em',
              mb: 1,
              display: 'block',
            }}
          >
            TECHNICAL EXPERTISE
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Skills & Technologies
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto',
              fontSize: '1.0625rem',
            }}
          >
            A comprehensive toolkit for building modern, scalable applications
          </Typography>
        </Box>

        {/* Skills Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
        >
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {skillCategories.map((category, categoryIndex) => (
              <Grid item xs={12} sm={category.wide ? 12 : 6} md={category.wide ? 12 : 4} key={categoryIndex}>
                <motion.div variants={categoryVariants}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 3, md: 4 },
                      height: '100%',
                      background: (theme) =>
                        alpha(theme.palette.background.paper, 0.6),
                      backdropFilter: 'blur(12px)',
                      border: (theme) =>
                        `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        borderColor: (theme) => alpha(category.color, 0.3),
                        boxShadow: (theme) =>
                          `0 20px 25px -5px ${alpha(
                            '#000000',
                            0.25
                          )}, 0 0 20px ${alpha(category.color, 0.15)}`,
                        '&::before': {
                          opacity: 1,
                        },
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, ${category.color}, ${alpha(
                          category.color,
                          0.5
                        )})`,
                        opacity: 0.6,
                        transition: 'opacity 0.3s ease',
                      },
                    }}
                  >
                    {/* Category Header */}
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: category.color,
                            boxShadow: `0 0 12px ${alpha(category.color, 0.6)}`,
                          }}
                        />
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            fontSize: '1.125rem',
                            color: 'text.primary',
                          }}
                        >
                          {category.title}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '40px',
                          height: '2px',
                          background: `linear-gradient(90deg, ${category.color}, transparent)`,
                          borderRadius: 1,
                        }}
                      />
                    </Box>

                    {/* Skills List */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                      }}
                    >
                      {category.skills.map((skill, skillIndex) => (
                        <motion.div
                          key={skillIndex}
                          variants={skillVariants}
                          whileHover={{
                            scale: 1.05,
                            transition: {
                              type: 'spring',
                              stiffness: 400,
                              damping: 10,
                            },
                          }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Box
                            sx={{
                              px: 2,
                              py: 1,
                              borderRadius: 2,
                              backgroundColor: (theme) =>
                                alpha(category.color, 0.1),
                              border: (theme) =>
                                `1.5px solid ${alpha(category.color, 0.2)}`,
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              color: 'text.primary',
                              cursor: 'default',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              backdropFilter: 'blur(4px)',
                              '&:hover': {
                                backgroundColor: (theme) =>
                                  alpha(category.color, 0.15),
                                borderColor: (theme) =>
                                  alpha(category.color, 0.4),
                                boxShadow: `0 4px 12px ${alpha(
                                  category.color,
                                  0.2
                                )}`,
                              },
                            }}
                          >
                            {skill}
                          </Box>
                        </motion.div>
                      ))}
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Bottom Accent */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              mt: 6,
              height: '2px',
              background:
                'linear-gradient(90deg, transparent, #6366f1, #06b6d4, transparent)',
              borderRadius: 1,
              opacity: 0.3,
            }}
          />
        </motion.div>
      </motion.div>
    </Container>
  );
}

export default Skills;